/**
 * Hook: useApiOrchestrator
 *
 * Evaluates API contracts on step submit and fires any that are newly
 * satisfiable. Tracks fulfillment in a session-persisted store.
 */
import { useCallback } from "react";
import { useStore } from "effector-react";
import {
  checkPrerequisites,
  buildContracts,
  type ContractExecutors,
} from "./api-contracts";
import {
  $contractFulfillment,
  contractFulfilled,
} from "./contract-fulfillment-store";

export type OrchestratorResult = {
  fired: string[];
  errors: Array<{ contractId: string; error: unknown }>;
};

export const useApiOrchestrator = (executors: ContractExecutors) => {
  const fulfillment = useStore($contractFulfillment);

  const evaluateContracts =
    useCallback(async (): Promise<OrchestratorResult> => {
      const contracts = buildContracts(executors);
      const result: OrchestratorResult = { fired: [], errors: [] };

      // Build a set of fulfilled contract IDs (including ones just fired this pass)
      const fulfilled = new Set(
        Object.entries(fulfillment)
          .filter(([, v]) => v)
          .map(([k]) => k),
      );

      // Evaluate contracts in dependency order
      // Simple approach: iterate multiple times until no new contracts fire
      let progress = true;
      while (progress) {
        progress = false;

        for (const contract of contracts) {
          // Skip already fulfilled (for trackFulfillment contracts)
          if (contract.trackFulfillment && fulfilled.has(contract.id)) {
            console.debug(
              `[orchestrator] ${contract.id}: skip (already fulfilled)`,
            );
            continue;
          }

          // Skip if already fired this evaluation pass
          if (result.fired.includes(contract.id)) continue;

          // Check dependencies
          if (contract.dependsOn?.some((dep) => !fulfilled.has(dep))) {
            console.debug(
              `[orchestrator] ${contract.id}: skip (deps not met: ${contract.dependsOn?.filter((d) => !fulfilled.has(d)).join(", ")})`,
            );
            continue;
          }

          // Check prerequisites
          if (!checkPrerequisites(contract.prerequisites)) {
            console.debug(
              `[orchestrator] ${contract.id}: skip (prerequisites not met)`,
            );
            continue;
          }

          // Check runtime condition
          if (contract.condition && !contract.condition()) {
            console.debug(
              `[orchestrator] ${contract.id}: skip (condition false)`,
            );
            continue;
          }

          console.debug(`[orchestrator] ${contract.id}: firing`);

          // All checks passed — fire the executor
          try {
            await contract.executor();
            fulfilled.add(contract.id);
            result.fired.push(contract.id);

            if (contract.trackFulfillment) {
              contractFulfilled(contract.id);
            }

            progress = true;
          } catch (error) {
            result.errors.push({ contractId: contract.id, error });
            // Don't set progress=true on error — stop trying this contract
          }
        }
      }

      return result;
    }, [executors, fulfillment]);

  return { evaluateContracts };
};
